/**
 * UpdatePayments - Handles automatic title and URL title generation
 * for the commissioner's financial transactions form.
 * * Rules:
 * - Listens to 'change' events on the player dropdown.
 * - Listens to 'click' events on custom styled dropdown items.
 * - Slugs are generated dynamically to prevent validation errors.
 */
export default class UpdatePayments {
    constructor() {
        // Target specifically by ID
        this.form = document.getElementById('payment-form');
        this.playerSelect = document.getElementById('player');
        
        // Target the custom transaction type hidden field and the specific clickable list anchors
        this.transactionTypeInput = document.getElementById('transaction-type');
        this.transactionTypeItems = document.querySelectorAll('a.custom-transaction-type');
        this.transactionTypeBtnLabel = document.getElementById('selected-type-label');
        
        // Hidden EE Title Inputs for channel:form
        this.hiddenTitle = document.getElementById('hidden_title');
        this.hiddenUrlTitle = document.getElementById('hidden_url_title');
        
        // Hidden input to pass the raw screen_name to PHP POST
        this.playerScreenNameInput = document.getElementById('player_screen_name');
        
        // Read-only metadata from form hidden inputs
        this.leagueIdInput = document.getElementsByName('league_id')[0];
        this.weekInput = document.getElementsByName('week')[0];

        // Only initialize if the specific payment form is on the page
        if (this.form && this.playerSelect && this.transactionTypeInput) {
            this.init();
        }
    }

    /**
     * Set up event listeners and establish baseline values
     */
    init() {
        // Standard player dropdown listener
        this.playerSelect.addEventListener('change', () => this.generateEETitle());
        
        // Listen to clicks on the custom dropdown anchors
        this.transactionTypeItems.forEach(item => {
            item.addEventListener('click', (e) => this.handleTransactionTypeChange(e, item));
        });
        
        // Run once on load just to ensure fields are filled if browser remembers values
        this.generateEETitle();
    }

    /**
     * Handles UI updates and value setting when a custom dropdown item is clicked
     * @param {Event} e - The click event
     * @param {HTMLElement} item - The specific anchor tag clicked
     */
    handleTransactionTypeChange(e, item) {
        e.preventDefault(); // Stop page jumping from href="#"
        
        const value = item.getAttribute('data-value');
        
        // Pull out the text but strip the icon spacing/markup if any
        const titleElement = item.querySelector('.item-title');
        const label = titleElement ? titleElement.innerText.trim() : 'Select Type...';
        
        // 1. Set the value of the hidden text input
        this.transactionTypeInput.value = value;
        
        // 2. Visually update the button text to show what was selected
        if (this.transactionTypeBtnLabel) {
            this.transactionTypeBtnLabel.textContent = label;
        }
        
        // 3. Recalculate the URL slug
        this.generateEETitle();
    }

    /**
     * Core logic to build the readable Title and slugified URL Title
     */
    generateEETitle() {
        let playerName = 'User';
        let userID = '0';
        
        // 1. Grab the current selected player name and their entry_id (value)
        if (this.playerSelect.selectedIndex > 0) {
            const selectedOption = this.playerSelect.options[this.playerSelect.selectedIndex];
            
            playerName = selectedOption.text;
            userID = selectedOption.value; // Grabs the {entry_id} from the option's value
            
            // Map that exact name directly to the hidden input for PHP POST
            if (this.playerScreenNameInput) {
                this.playerScreenNameInput.value = playerName;
            }
        } else {
            // Reset the hidden input if they deselect
            if (this.playerScreenNameInput) {
                this.playerScreenNameInput.value = '';
            }
        }
            
        // 2. Grab value from the hidden text input
        const typeVal = this.transactionTypeInput.value !== '' ? this.transactionTypeInput.value : 'unknown';
        
        // 3. Grab pre-set variables
        const leagueId = this.leagueIdInput ? this.leagueIdInput.value : '0';
        const week = this.weekInput ? this.weekInput.value : '0';
        const timestamp = Math.floor(Date.now() / 1000);
        
        // 4. Build the readable Title (Featuring the player's entry_id)
        const titleString = `Commish Updated ${playerName} | ID:${userID} | Type: ${typeVal} | LeagueID: ${leagueId} | Week ${week} | ${timestamp}`;
        
        // 5. Build the slugified URL Title
        const urlTitleString = `commish_updated_${playerName.toLowerCase()}_id${userID}_type_${typeVal}_leagueid${leagueId}_week${week}_${timestamp}`
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '_') // Replace spaces & symbols with underscores
            .replace(/^_+|_+$/g, '');   // Trim trailing/leading underscores
            
        if (this.hiddenTitle && this.hiddenUrlTitle) {
            this.hiddenTitle.value = titleString;
            this.hiddenUrlTitle.value = urlTitleString;
        }
    }
}